import React, { createContext, useContext, useState, ReactNode } from "react";
import { getEnv } from "../lib/env";

// Feature flag to control whether to strictly enforce ProductForm provider usage
const ENABLE_STRICT_PRODUCT_FORM =
  getEnv("VITE_ENABLE_STRICT_PRODUCT_FORM", "true") === "true";

// Define the interface for the product form values
export interface CurrentProductFormValues {
  name?: string;
  hs_code?: string | null;
  ean?: number | null;
  gross_weight?: number | null;
  cbm?: number | null;
  value?: number | null;
  pieces?: number | null;
  cartons?: number | null;
}

interface ProductFormContextType {
  currentFormValues: CurrentProductFormValues;
  updateFormValues: (values: CurrentProductFormValues) => void;
  clearFormValues: () => void;
  isEditingProduct: boolean;
  setIsEditingProduct: (isEditing: boolean) => void;
}

const ProductFormContext = createContext<ProductFormContextType | undefined>(
  undefined
);

interface ProductFormProviderProps {
  children: ReactNode;
}

export const ProductFormProvider: React.FC<ProductFormProviderProps> = ({
  children,
}) => {
  const [currentFormValues, setCurrentFormValues] =
    useState<CurrentProductFormValues>({});
  const [isEditingProduct, setIsEditingProduct] = useState(false);

  const updateFormValues = (values: CurrentProductFormValues) => {
    setCurrentFormValues((prev) => ({
      ...prev,
      ...values,
    }));
  };

  const clearFormValues = () => {
    setCurrentFormValues({});
  };

  return (
    <ProductFormContext.Provider
      value={{
        currentFormValues,
        updateFormValues,
        clearFormValues,
        isEditingProduct,
        setIsEditingProduct,
      }}
    >
      {children}
    </ProductFormContext.Provider>
  );
};

export const useProductForm = () => {
  const context = useContext(ProductFormContext);

  // Only throw an error if the feature flag is enabled
  if (!context && ENABLE_STRICT_PRODUCT_FORM) {
    throw new Error("useProductForm must be used within a ProductFormProvider");
  }

  // Return context or defaults
  return (
    context || {
      currentFormValues: {},
      updateFormValues: () => {},
      clearFormValues: () => {},
      isEditingProduct: false,
      setIsEditingProduct: () => {},
    }
  );
};
